import { useState, useEffect } from "react";
import { UserPlus, Shield, Bell } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { PageHeader } from "@/components/library/layout";
import { Badge, Avatar } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ASSIGNABLE_ROLES, can, tierOf } from "@/lib/permissions";
import { ConfirmDelete } from "@/components/shared/ConfirmDelete";
import { Switch } from "@/components/ui/switch";
import { useStore, getNotificationsEnabled, setNotificationsEnabled } from "@/lib/dataStore";
import { setStaffPin, listStaffPins } from "@/lib/pinAuth";
import { KeyRound } from "lucide-react";

const TEAM_KEY = "bo_team_v1";
const SEED = [
  { id: "u-owner", name: "Drottningen", email: "agare@drottningensbo.se", role: "owner" },
  { id: "u-chef", name: "Sara Berg", email: "chef@drottningensbo.se", role: "chef" },
  { id: "u-skift", name: "Marcus Holm", email: "skiftledare@drottningensbo.se", role: "shift_leader" },
  { id: "u-anna", name: "Anna Lind", email: "personal@drottningensbo.se", role: "cleaning" },
  { id: "u-jonas", name: "Jonas Ek", email: "kok@drottningensbo.se", role: "kitchen" },
];
const load = () => { try { return JSON.parse(localStorage.getItem(TEAM_KEY)) || SEED; } catch (_) { return SEED; } };
const save = (t) => localStorage.setItem(TEAM_KEY, JSON.stringify(t));

// capability matrix rows
const MATRIX = [
  ["checklist.complete", "Genomföra checklistor"],
  ["incident.create", "Rapportera problem"],
  ["task.create", "Skapa uppgifter"],
  ["manual.create", "Publicera manualer"],
  ["task.delete", "Ta bort uppgifter"],
  ["audit.read", "Se historik/logg"],
  ["user.manage", "Hantera användare & roller"],
];

export default function Admin() {
  useStore();
  const { hasPermission, currentOrganization, currentRestaurant, profile } = useSbAuth();
  const rid = currentRestaurant?.id;
  const [team, setTeam] = useState(load);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "cleaning" });
  useEffect(() => save(team), [team]);
  const notifsOn = rid ? getNotificationsEnabled(rid) : true;

  const toggleNotifs = (val) => {
    setNotificationsEnabled(rid, val, profile?.full_name || "Ägare");
    toast.success(val ? "Notiser påslagna" : "Notiser avstängda (riktade person-/avdelningsnotiser går fortfarande fram)");
  };

  const canManage = hasPermission("role.manage");
  const roleLabel = (r) => ASSIGNABLE_ROLES.find((x) => x.key === r)?.label || r;

  const invite = () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Fyll i namn och e-post");
    setTeam((t) => [...t, { id: "u" + Date.now(), ...f }]);
    toast.success(`${f.name} inbjuden (mock)`);
    setOpen(false); setF({ name: "", email: "", role: "cleaning" });
  };
  const changeRole = (id, role) => { setTeam((t) => t.map((u) => (u.id === id ? { ...u, role } : u))); toast.success("Roll uppdaterad"); };
  const removeUser = (id) => setTeam((t) => t.filter((u) => u.id !== id));

  return (
    <div className="max-w-4xl mx-auto p-6 sm:p-10 animate-fade-up" data-testid="admin-page">
      <PageHeader title="Roller & användare"
        description="Bjud in personal, tilldela roller per restaurang och se exakt vad varje roll får göra."
        actions={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-primary text-primary-foreground hover:opacity-90" data-testid="invite-user"><UserPlus className="h-4 w-4 mr-1" />Bjud in</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Bjud in användare</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Namn</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" /></div>
                <div className="space-y-1.5"><Label>E-post</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" /></div>
                <div className="space-y-1.5"><Label>Roll</Label>
                  <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSIGNABLE_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button onClick={invite} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="send-invite">Skicka inbjudan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <div className="bg-card border border-border rounded-2xl divide-y divide-border">
        {team.map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`team-${u.id}`}>
            <Avatar name={u.name} size={38} />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground truncate font-mono">{u.email}</p>
            </div>
            {canManage ? (
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                <SelectTrigger className="w-40 h-9" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ASSIGNABLE_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Badge tone="accent">{roleLabel(u.role)}</Badge>}
            {canManage && (
              <ConfirmDelete
                title="Ta bort användare?"
                description={`${u.name} tas bort från teamet.`}
                onConfirm={() => removeUser(u.id)}
                trigger={
                  <button className="text-xs text-muted-foreground hover:text-[hsl(0_80%_72%)]" data-testid={`remove-${u.id}`}>Ta bort</button>
                }
              />
            )}
          </div>
        ))}
        {team.length === 0 && <div className="p-6"><EmptyState title="Inga användare" /></div>}
      </div>

      {canManage && currentRestaurant && <PinManager restaurantId={currentRestaurant.id} staff={team} />}

      {canManage && (
        <div className="mt-10" data-testid="notification-settings">
          <p className="overline text-muted-foreground mb-4 flex items-center gap-2"><Bell className="h-3.5 w-3.5" />Inställningar</p>
          <div className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium">Notiser</p>
              <p className="text-sm text-muted-foreground mt-0.5">Slå av allmänna notiser för hela restaurangen (t.ex. faktura godkänd, prisavvikelser). Notiser riktade till en person eller avdelning går alltid fram.</p>
            </div>
            <Switch checked={notifsOn} onCheckedChange={toggleNotifs} data-testid="notifications-toggle" />
          </div>
        </div>
      )}

      <div className="mt-10">
        <p className="overline text-muted-foreground mb-4 flex items-center gap-2"><Shield className="h-3.5 w-3.5" />Behörighetsmatris</p>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Behörighet</th>
              {ASSIGNABLE_ROLES.map((r) => <th key={r.key} className="px-3 py-3 font-medium text-center whitespace-nowrap">{r.label}</th>)}
            </tr></thead>
            <tbody>
              {MATRIX.map(([perm, label]) => (
                <tr key={perm} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">{label}</td>
                  {ASSIGNABLE_ROLES.map((r) => (
                    <td key={r.key} className="px-3 py-3 text-center">
                      {can(r.key, perm) ? <span className="text-[hsl(145_60%_60%)]">●</span> : <span className="text-border">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PinManager({ restaurantId, staff }) {
  const [pins, setPins] = useState([]);
  const [email, setEmail] = useState("");
  const [initials, setInitials] = useState("");
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = () => listStaffPins(restaurantId).then(setPins).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [restaurantId]);

  const save = async () => {
    if (!email) return toast.error("Välj personal");
    if (!initials.trim()) return toast.error("Ange initialer");
    if (!(pin.length === 4)) return toast.error("PIN måste vara 4 siffror");
    setSaving(true);
    try {
      await setStaffPin({ restaurantId, email, initials: initials.trim().toUpperCase(), pin });
      toast.success("PIN sparad");
      setPin(""); setInitials(""); setEmail("");
      refresh();
    } catch (e) { toast.error(e.message || "Kunde inte spara PIN"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-10" data-testid="pin-manager">
      <p className="overline text-muted-foreground mb-4 flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" />PIN-koder · snabbinloggning</p>
      <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
        <p className="text-sm text-muted-foreground">Sätt en 4-siffrig PIN så personalen kan snabblogga in med initialer + PIN på en delad surfplatta.</p>
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="space-y-1.5 sm:col-span-2"><Label>Personal</Label>
            <Select value={email} onValueChange={setEmail}>
              <SelectTrigger data-testid="pin-mgr-email"><SelectValue placeholder="Välj personal" /></SelectTrigger>
              <SelectContent>{staff.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Initialer</Label>
            <Input value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase())} placeholder="AL" maxLength={4} className="uppercase" data-testid="pin-mgr-initials" /></div>
          <div className="space-y-1.5"><Label>PIN (4 siffror)</Label>
            <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" type="password" placeholder="••••" data-testid="pin-mgr-code" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="pin-mgr-save">Spara PIN</Button>

        {pins.length > 0 && (
          <div className="border-t border-border pt-4">
            <p className="text-xs text-muted-foreground mb-2">Aktiva PIN-koder ({pins.length})</p>
            <div className="flex flex-wrap gap-2">
              {pins.map((p) => (
                <span key={p.initials} className="text-xs bg-secondary rounded-lg px-3 py-1.5 font-mono" data-testid={`pin-active-${p.initials}`}>{p.initials} · {p.email}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
